import { useState } from 'react';

export default function HonorCode() {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const principles = [
    {
      title: 'We Tell the Truth',
      icon: 'ri-chat-check-line',
      content: "We don't exaggerate. We don't hide details. And we don't say what sounds good if it isn't accurate.\n\nHomeowners deserve honesty in pricing, recommendations, timelines, and outcomes.\n\nThat's non-negotiable.",
      gradient: 'from-[#52AB98] to-[#2B6777]'
    },
    {
      title: 'We Do What\'s Right',
      icon: 'ri-compass-line',
      content: "If something isn't in the homeowner's best interest, we don't do it — even when it's harder, slower, or costs us more.\n\nHonor means choosing the right path, every time.",
      gradient: 'from-[#2B6777] to-[#52AB98]'
    },
    {
      title: 'We Respect Your Home',
      icon: 'ri-home-heart-line',
      content: "Your home is personal. We treat it that way.\n\nFrom how we communicate, to how we measure, install, and follow through — professionalism and respect are expected, not optional.",
      gradient: 'from-[#52AB98] to-[#F6D7DF]'
    },
    {
      title: 'We Take Responsibility',
      icon: 'ri-shield-check-line',
      content: "We don't pass blame or hide behind fine print.\n\nWe verify measurements.\nWe stand behind our work.\nAnd we remain accountable long after installation is complete.",
      gradient: 'from-[#F6D7DF] to-[#52AB98]'
    },
    {
      title: 'We Earn Trust Through Action',
      icon: 'ri-hand-heart-line',
      content: "Trust isn't claimed; it's earned through consistency, transparency, and follow-through.\n\nEvery part of our process is designed to prove we deserve it.",
      gradient: 'from-[#2B6777] to-[#F6D7DF]'
    }
  ];

  const toggleAccordion = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="honor-code" className="relative py-20 md:py-28 overflow-hidden">
      {/* Soft Gradient Background */}
      <div className="absolute inset-0 bg-gradient-to-b from-white via-[#C8D8E4]/20 to-white"></div>
      
      {/* Decorative Background Elements */}
      <div className="absolute top-20 right-10 w-96 h-96 bg-[#52AB98]/10 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute bottom-20 left-10 w-80 h-80 bg-[#F6D7DF]/15 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative max-w-[1100px] mx-auto px-6">
        {/* Section Badge */}
        <div className="text-center mb-6">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full border border-[#2B6777]/10 shadow-lg">
            <div className="w-2 h-2 rounded-full bg-[#52AB98] animate-pulse"></div>
            <span className="text-sm font-medium text-[#2B6777] uppercase tracking-wide">
              Our Honor Code
            </span>
          </div>
        </div>

        {/* Header */}
        <div className="text-center max-w-[900px] mx-auto mb-16">
          <div className="flex items-center justify-center gap-6 mb-6">
            <img 
              src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/9c268622345bd3468c76ee1adfcd1aa7.png" 
              alt="UVA Logo" 
              className="w-20 h-20 md:w-24 md:h-24 object-contain"
            />
            <h2 className="text-5xl md:text-6xl font-heading font-bold text-[#2B6777] tracking-tight">
              Our Honor Code
            </h2>
          </div>
          <p className="text-xl md:text-2xl text-[#2B6777]/80 leading-relaxed mb-4">
            A set of principles we live by — personally and professionally.
          </p>
          <p className="text-lg text-[#2B6777]/70 leading-relaxed mb-6">
            At Snapwindows, how we do business matters just as much as the windows we install.
          </p>
          <p className="text-lg text-[#2B6777]/70 leading-relaxed">
            Like the Honor tradition many of us carry from the University of Virginia — built on integrity, trust, and accountability, our Honor Code reflects the standards we hold ourselves to in every decision, every day.
          </p>
        </div>

        {/* Emphasis Statement */}
        <div className="max-w-[800px] mx-auto mb-16">
          <div className="flex justify-center mb-8">
            <img 
              src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/da3f45283dde3421a52ea41a6bce2f15.png" 
              alt="Snapwindows.ai Logo" 
              className="w-24 h-24 object-contain"
            />
          </div>
          <div className="relative p-8 bg-gradient-to-br from-white to-[#52AB98]/5 rounded-3xl border-2 border-[#52AB98]/20 shadow-xl">
            <div className="absolute -top-4 left-1/2 -translate-x-1/2 px-6 py-2 bg-gradient-to-r from-[#2B6777] to-[#52AB98] rounded-full shadow-lg">
              <span className="text-sm font-semibold text-white uppercase tracking-wide">
                Our Foundation
              </span>
            </div>
            <div className="text-center pt-4">
              <p className="text-2xl md:text-3xl font-heading font-bold text-[#2B6777] mb-3">
                Honor isn't a slogan here.
              </p>
              <p className="text-xl text-[#2B6777]/80">
                It's how we operate even when no one's watching.
              </p>
            </div>
          </div>
        </div>

        {/* Section Title */}
        <div className="text-center mb-12">
          <h3 className="text-3xl md:text-4xl font-heading font-bold text-[#2B6777]">
            The Snapwindows Honor Code
          </h3>
        </div>

        {/* Premium Accordion */}
        <div className="max-w-[900px] mx-auto space-y-4 mb-16">
          {principles.map((principle, index) => (
            <div
              key={index}
              className="group relative bg-white rounded-2xl border border-[#2B6777]/10 shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
            >
              {/* Accordion Header */}
              <button
                onClick={() => toggleAccordion(index)}
                className="w-full flex items-center justify-between p-6 md:p-8 text-left cursor-pointer"
              >
                <div className="flex items-center gap-4 flex-1">
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${principle.gradient} flex items-center justify-center shadow-lg flex-shrink-0 transition-transform duration-500 ${openIndex === index ? 'scale-110 rotate-6' : 'scale-100 rotate-0'}`}>
                    <i className={`${principle.icon} text-white text-2xl`}></i>
                  </div>

                  {/* Title */}
                  <h4 className="text-xl md:text-2xl font-heading font-bold text-[#2B6777] pr-4">
                    {principle.title}
                  </h4>
                </div>

                {/* Toggle Icon */}
                <div className={`w-10 h-10 rounded-full bg-[#52AB98]/10 flex items-center justify-center flex-shrink-0 transition-all duration-500 ${openIndex === index ? 'rotate-180 bg-[#52AB98]/20' : 'rotate-0'}`}>
                  <i className="ri-arrow-down-s-line text-2xl text-[#2B6777]"></i>
                </div>
              </button>

              {/* Accordion Content */}
              <div
                className={`overflow-hidden transition-all duration-500 ease-in-out ${
                  openIndex === index ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
                }`}
              >
                <div className="px-6 md:px-8 pb-8 md:pl-[104px]">
                  <div className="relative pl-6 border-l-2 border-[#52AB98]/30">
                    {principle.content.split('\n\n').map((paragraph, pIndex) => (
                      <p
                        key={pIndex}
                        className="text-lg text-[#2B6777]/80 leading-relaxed mb-4 last:mb-0"
                      >
                        {paragraph}
                      </p>
                    ))}
                  </div>
                </div>
              </div>

              {/* Decorative Gradient Line */}
              <div
                className={`h-1 bg-gradient-to-r ${principle.gradient} transition-all duration-500 ${
                  openIndex === index ? 'opacity-100' : 'opacity-0'
                }`}
              ></div>
            </div>
          ))}
        </div>

        {/* Closing Statement */}
        <div className="max-w-[800px] mx-auto">
          <div className="relative p-10 md:p-12 bg-gradient-to-br from-[#2B6777] to-[#52AB98] rounded-3xl shadow-2xl overflow-hidden">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-48 h-48 bg-white/5 rounded-full blur-2xl"></div>

            <div className="relative text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/20 backdrop-blur-sm mb-6">
                <img 
                  src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/da3f45283dde3421a52ea41a6bce2f15.png" 
                  alt="SnapWindows Logo" 
                  className="w-10 h-10 object-contain"
                />
              </div>
              
              <p className="text-3xl md:text-4xl font-heading font-bold text-white mb-4 leading-tight">
                Honor isn't what we say.
              </p>
              <p className="text-xl md:text-2xl text-white/90 leading-relaxed">
                It's how we show up — every homeowner, every project, every time.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
